import { create } from "zustand";

// The pop counter. IMPORTANT: 3D/HUD code must never read this via a React
// hook per pop — use usePopStore.subscribe + direct DOM writes (see
// PopCounterHud) so 20 pops/sec never re-render the tree above <Canvas>.

/**
 * The visitor's own popping record, persisted so a refresh doesn't erase who
 * they are. A memento, not a ledger: `last` is optimistic (see myLastPop),
 * `count` only counts pops made on this device, and `firstAt` remembers the
 * moment of their very first pop (absent for records from before it shipped —
 * it backfills on their next pop, which is honest enough for a memento).
 */
type PopperRecord = { last: number; count: number; firstAt?: number };

const POPPER_KEY = "poparium:popper";

function loadPopper(): PopperRecord | null {
  try {
    const raw = localStorage.getItem(POPPER_KEY);
    if (!raw) return null;
    const v: unknown = JSON.parse(raw);
    const last = (v as Partial<PopperRecord>)?.last;
    const count = (v as Partial<PopperRecord>)?.count;
    const firstAt = (v as Partial<PopperRecord>)?.firstAt;
    if (typeof last === "number" && last >= 1 && typeof count === "number" && count >= 0) {
      const rec: PopperRecord = { last: Math.floor(last), count: Math.floor(count) };
      if (typeof firstAt === "number" && firstAt > 0) rec.firstAt = Math.floor(firstAt);
      return rec;
    }
  } catch {
    /* no storage, or someone else's JSON — start fresh */
  }
  return null;
}

function savePopper(rec: PopperRecord): void {
  try {
    localStorage.setItem(POPPER_KEY, JSON.stringify(rec));
  } catch {
    /* storage full or blocked — the session still works, just forgets */
  }
}

const stored = typeof localStorage === "undefined" ? null : loadPopper();

type PopState = {
  /** Displayed total — the Grand Tally. */
  total: number;
  /** Concurrent visitors, per server sync */
  live: number;
  /**
   * The Grand Tally as it stood right after this visitor's most recent pop —
   * the number their share card commemorates. Null until they pop something.
   * Optimistic, so it can lag the authoritative total slightly; the card is a
   * memento, not a receipt. Survives reloads via localStorage.
   */
  myLastPop: number | null;
  /** Pops this visitor has made on this device, ever. */
  myPopCount: number;
  /** When their first pop on this device happened (ms), null before it. */
  myFirstPopAt: number | null;
  /**
   * Where the server says this visitor's pops land on the country standings
   * (ISO alpha-2, "XX" when unattributable). Null until the first WS init.
   */
  myCountry: string | null;
  connection: "offline" | "connecting" | "open" | "fallback";
  addPops: (n: number) => void;
  setConnection: (c: PopState["connection"]) => void;
  setMyCountry: (c: string) => void;
  /** Server reconcile — never lets the number go backwards */
  syncTotal: (serverTotal: number, live: number) => void;
};

export const usePopStore = create<PopState>()((set) => ({
  total: 0,
  live: 0,
  myLastPop: stored?.last ?? null,
  myPopCount: stored?.count ?? 0,
  myFirstPopAt: stored?.firstAt ?? null,
  myCountry: null,
  connection: "offline",
  addPops: (n) =>
    set((s) => {
      const last = s.total + n;
      const count = s.myPopCount + n;
      const firstAt = s.myFirstPopAt ?? Date.now();
      savePopper({ last, count, firstAt });
      return { total: s.total + n, myLastPop: last, myPopCount: count, myFirstPopAt: firstAt };
    }),
  setConnection: (connection) => set({ connection }),
  setMyCountry: (myCountry) => set({ myCountry }),
  syncTotal: (serverTotal, live) =>
    set((s) => ({ total: Math.max(s.total, serverTotal), live })),
}));
